import { useCallback, useEffect, useRef, useState } from "react";

const STICKY_OFFSET = 129;
const DETECTION_LINE = STICKY_OFFSET + 50;
const PAGE_BOTTOM_TOLERANCE = 20;
const SCROLL_LOCK_MS = 800;

type CategoryRef = { id: string };

/**
 * Scroll-spy over category sections (elements whose DOM id is the category id) under a sticky nav.
 * Returns the category in view, a smooth-scroll click handler and the detector to call on window scroll.
 */
export function useCategoryScrollSpy(categories: readonly CategoryRef[]) {
  const [categoryInViewId, setCategoryInViewId] = useState("");
  const isScrollingToCategory = useRef(false);
  const scrollTimeoutRef = useRef<number | null>(null);

  const detectActiveCategory = useCallback(() => {
    if (isScrollingToCategory.current || categories.length === 0) return;

    const scrollPosition = window.innerHeight + window.scrollY;
    const pageBottom = document.body.offsetHeight - PAGE_BOTTOM_TOLERANCE;
    if (scrollPosition >= pageBottom) {
      setCategoryInViewId(categories[categories.length - 1].id);
      return;
    }

    let activeCategoryId = categories[0].id;

    for (const cat of categories) {
      const el = document.getElementById(cat.id);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (rect.top <= DETECTION_LINE) {
        activeCategoryId = cat.id;
      }
    }

    setCategoryInViewId(activeCategoryId);
  }, [categories]);

  const handleCategoryClick = useCallback((categoryId: string) => {
    const el = document.getElementById(categoryId);
    if (!el) return;

    const y = el.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET;

    isScrollingToCategory.current = true;
    setCategoryInViewId(categoryId);
    window.scrollTo({ top: y, behavior: "smooth" });

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = window.setTimeout(() => {
      isScrollingToCategory.current = false;
    }, SCROLL_LOCK_MS);
  }, []);

  useEffect(() => {
    setCategoryInViewId((current) =>
      categories.some((c) => c.id === current) ? current : (categories[0]?.id ?? ""),
    );
  }, [categories]);

  return { categoryInViewId, detectActiveCategory, handleCategoryClick };
}
